// @@TECH_PACKS_v94@@ Shared tech-pack UI bits (HQ editor, client portal tab, public share page).
import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

export const STATUS_TONE = { draft: 'grey', review: 'amber', approved: 'green', changes_requested: 'red' };
export const STATUS_LABEL = { draft: 'Draft', review: 'Awaiting client review', approved: 'Approved', changes_requested: 'Changes requested' };

export function fmtBytes(n) {
  n = Number(n || 0);
  if (n >= 1048576) return (n / 1048576).toFixed(1) + ' MB';
  if (n >= 1024) return Math.round(n / 1024) + ' KB';
  return n + ' B';
}
// YYYY-MM-DD -> local date (never new Date('YYYY-MM-DD'), which is UTC).
export function fmtYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  if (!m) return '—';
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}
export function fmtTs(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) { return '—'; }
}
export const num = (v) => { const n = Number(v); return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100); };
export function inkLabel(c) { return [c.pantone ? 'PMS ' + c.pantone : null, c.name].filter(Boolean).join(' — ') || '—'; }
export function blankText(b) {
  if (!b) return '—';
  const head = [b.brand, b.style].filter(Boolean).join(' ');
  return [head, b.name].filter(Boolean).join(' — ') || '—';
}

export function Swatch({ hex, size = 18, title }) {
  const style = {
    display: 'inline-block', width: size, height: size, borderRadius: 4, flexShrink: 0, verticalAlign: 'middle',
    border: '1px solid rgba(128,128,128,.45)',
    background: hex || 'repeating-linear-gradient(45deg,#fff 0 3px,#ddd 3px 6px)',
  };
  return <span style={style} title={title || hex || 'no swatch'} />;
}

// Authenticated images: fetch with the Bearer token, render from a blob URL.
const _blobCache = new Map();
export function useAuthedUrl(path) {
  const [url, setUrl] = useState(() => (path && _blobCache.get(path)) || '');
  useEffect(() => {
    if (!path) { setUrl(''); return undefined; }
    if (_blobCache.has(path)) { setUrl(_blobCache.get(path)); return undefined; }
    let on = true;
    apiFetch(path).then((r) => (r.ok ? r.blob() : null)).then((b) => {
      if (!b || !on) return;
      const u = URL.createObjectURL(b);
      _blobCache.set(path, u);
      setUrl(u);
    }).catch(() => {});
    return () => { on = false; };
  }, [path]);
  return url;
}
export function AuthedImg({ path, alt, style, className }) {
  const url = useAuthedUrl(path);
  if (!url) return <div className={className} style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, opacity: 0.6 }}>…</div>;
  return <img src={url} alt={alt || ''} style={style} className={className} />;
}

// Download a Bearer-authenticated file (a plain <a href> would skip the header).
export async function downloadAuthed(path, fallbackName) {
  const r = await apiFetch(path);
  if (!r.ok) {
    let msg = 'Download failed.';
    try { const d = await r.json(); if (d && d.error) msg = d.error; } catch (e) { /* binary */ }
    throw new Error(msg);
  }
  const cd = r.headers.get('Content-Disposition') || '';
  const m = /filename\*=UTF-8''([^;]+)/i.exec(cd) || /filename="([^"]+)"/i.exec(cd);
  let name = fallbackName || 'download';
  if (m) { try { name = decodeURIComponent(m[1]); } catch (e) { name = m[1]; } }
  const blob = await r.blob();
  const u = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = u; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(u), 4000);
}

// ── Placement preview (mirrors the PDF diagram; units = inches) ─────────────
// Adult L flat: 30" incl. sleeves, body x 5..25, high-point shoulder y 0.5, hem y 30.5.
const GREEN = '#31b44b';
function shirtPath(back) {
  const dip = back ? 1.2 : 3;
  const cy = dip * 1.9 - 0.5;
  return {
    dip,
    d: `M12.4 0.5 Q15 ${cy} 17.6 0.5 L22.5 1.6 L29.4 8 L26.6 11.2 L25 9.6 L25 30.5 L5 30.5 L5 9.6 L3.4 11.2 L0.6 8 L7.5 1.6 Z`,
    collar: `M12.4 0.5 Q15 ${cy} 17.6 0.5`,
  };
}
export function PlacementPreview({ loc, artUrl, height = 260, dark = false }) {
  const garment = dark ? '#2a2a30' : '#ececee';
  const edge = dark ? '#5a5a62' : '#a3a3a9';
  const txt = dark ? '#d4d4d8' : '#141417';
  const W = Number(loc.width_in) || 0, H = Number(loc.height_in) || 0;
  const from = loc.from_ref_in == null || loc.from_ref_in === '' ? null : Number(loc.from_ref_in);
  const off = Number(loc.offset_x_in) || 0;
  const label = W && H ? `${num(W)}″ × ${num(H)}″` : '';

  const box = (bx, by) => (
    <g>
      {artUrl
        ? <image href={artUrl} x={bx} y={by} width={W} height={H} preserveAspectRatio="xMidYMid meet" />
        : <rect x={bx} y={by} width={W} height={H} fill={GREEN} fillOpacity="0.2" />}
      <rect x={bx} y={by} width={W} height={H} fill="none" stroke={GREEN} strokeWidth="0.14" strokeDasharray="0.45 0.3" />
      <text x={bx + W / 2} y={by + H + 1.1} fontSize="0.95" fontWeight="700" textAnchor="middle" fill={GREEN}>{label}</text>
    </g>
  );
  const dim = (x, y1, y2, t) => (Math.abs(y2 - y1) < 0.3 ? null : (
    <g stroke={txt} strokeWidth="0.08">
      <line x1={x} y1={y1} x2={x} y2={y2} /><line x1={x - 0.35} y1={y1} x2={x + 0.35} y2={y1} /><line x1={x - 0.35} y1={y2} x2={x + 0.35} y2={y2} />
      <text x={x - 0.5} y={(y1 + y2) / 2 + 0.3} fontSize="0.9" fontWeight="700" textAnchor="end" fill={txt} stroke="none">{t}</text>
    </g>
  ));

  if (loc.side === 'front' || loc.side === 'back') {
    const back = loc.side === 'back';
    const s = shirtPath(back);
    let top = null; let refY = null;
    if (W && H) {
      if (loc.ref_point === 'collar') { refY = s.dip; top = s.dip + (from || 0); }
      else if (loc.ref_point === 'shoulder') { refY = 0.5; top = 0.5 + (from || 0); }
      else if (loc.ref_point === 'hem') { refY = 30.5; top = 30.5 - (from || 0) - H; }
      else top = (s.dip + 30.5) / 2 - H / 2;
    }
    const cx = 15 + (back ? -off : off);
    const bx = cx - W / 2;
    return (
      <svg viewBox="-1.5 -1 33 33.5" style={{ width: '100%', height, display: 'block' }} role="img" aria-label={`${loc.side} placement`}>
        <path d={s.d} fill={garment} stroke={edge} strokeWidth="0.12" strokeLinejoin="round" />
        <path d={s.collar} fill="none" stroke={edge} strokeWidth="0.35" opacity="0.5" />
        {off !== 0 && <line x1="15" y1={s.dip + 0.5} x2="15" y2="30" stroke={edge} strokeWidth="0.06" strokeDasharray="0.3 0.3" />}
        {top != null && box(bx, top)}
        {top != null && refY != null && from != null && dim(bx - 0.7, Math.min(refY, loc.ref_point === 'hem' ? top + H : top), Math.max(refY, loc.ref_point === 'hem' ? top + H : top), num(from) + '″')}
        <text x="15" y="32.3" fontSize="0.9" textAnchor="middle" fill={edge}>{back ? 'BACK' : 'FRONT'}</text>
      </svg>
    );
  }
  if (loc.side === 'sleeve') {
    const len = Math.max(10, (from || 0) + H + 2);
    let top = null;
    if (W && H) {
      if (loc.ref_point === 'sleeve_hem' || loc.ref_point === 'hem') top = len - (from || 0) - H;
      else if (loc.ref_point === 'center' || from == null) top = (len - H) / 2;
      else top = from || 0;
    }
    const bx = 5 + off - W / 2;
    const hemRef = loc.ref_point === 'sleeve_hem' || loc.ref_point === 'hem';
    return (
      <svg viewBox={`-3 -1.6 16 ${len + 3.4}`} style={{ width: '100%', height, display: 'block' }} role="img" aria-label="sleeve placement">
        <path d={`M0 0 L10 0 L9 ${len} L1 ${len} Z`} fill={garment} stroke={edge} strokeWidth="0.1" />
        <text x="5" y="-0.5" fontSize="0.7" textAnchor="middle" fill={edge}>shoulder seam</text>
        <text x="5" y={len + 1} fontSize="0.7" textAnchor="middle" fill={edge}>sleeve hem</text>
        {top != null && box(bx, top)}
        {top != null && from != null && loc.ref_point !== 'center' && dim(bx - 0.6, hemRef ? top + H : 0, hemRef ? len : top, num(from) + '″')}
      </svg>
    );
  }
  // other: to-scale box only
  const vb = Math.max(W, H, 4) + 3;
  return (
    <svg viewBox={`${-vb / 2} ${-vb / 2} ${vb} ${vb + 1}`} style={{ width: '100%', height, display: 'block' }} role="img" aria-label="placement">
      {W && H ? box(-W / 2, -H / 2) : <text x="0" y="0" fontSize="0.8" textAnchor="middle" fill={edge}>Placement per notes</text>}
    </svg>
  );
}
